<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { announcementsApi, type AnnouncementCategory } from '@/api/announcements';
import { announcementViewsApi } from '@/api/announcement-views';
import { useMobile } from '@/composables/useMobile';

const { t } = useI18n();
const { isPhone } = useMobile();
// numeric width required: a percentage breaks the drawer's closed-state
// transform math, leaving it stuck open
const { width: viewportWidth } = useDisplay();

const FIVE_MINUTES_IN_MS = 300_000;

const drawerOpen = ref(false);
const queryClient = useQueryClient();

const { data, isPending: isAnnouncementsPending } = useQuery({
  queryKey: ['announcements'],
  queryFn: () => announcementsApi.index(),
  staleTime: FIVE_MINUTES_IN_MS,
});

const announcements = computed(() => data.value?.announcements ?? []);
const unreadCount = computed(() => data.value?.meta?.unreadCount ?? 0);

const { mutate: markSeen } = useMutation({
  mutationFn: () => announcementViewsApi.create(),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['announcements'] });
  },
});

const categoryColor: Record<AnnouncementCategory, string> = {
  feature: 'primary',
  improvement: 'info',
  fix: 'warning',
};

const openerAriaLabel = computed(() => {
  if (unreadCount.value > 0) {
    return t('announcements.openLabelWithUnread', { count: unreadCount.value });
  }
  return t('announcements.openLabel');
});

const hasMarkedSeen = ref(false);

watch([drawerOpen, unreadCount], ([isOpen, count]) => {
  if (isOpen && count > 0 && !hasMarkedSeen.value) {
    hasMarkedSeen.value = true;
    markSeen(undefined, {
      onError: () => { hasMarkedSeen.value = false; },
    });
  }
  if (count === 0) {
    hasMarkedSeen.value = false;
  }
});
</script>

<template>
  <v-badge
    :content="unreadCount"
    :model-value="unreadCount > 0"
    color="error"
    max="99"
    offset-x="10"
    offset-y="10"
    class="announcements-badge"
  >
    <v-btn
      icon="mdi-bullhorn-outline"
      size="large"
      color="primary"
      variant="text"
      :aria-label="openerAriaLabel"
      @click="drawerOpen = !drawerOpen"
    />
  </v-badge>

  <Teleport to="body">
    <v-navigation-drawer
      v-model="drawerOpen"
      location="right"
      temporary
      :width="isPhone ? viewportWidth : 420"
      border="0"
    >
      <v-toolbar
        flat
        density="comfortable"
        color="transparent"
      >
        <v-toolbar-title class="font-weight-bold">
          {{ t('announcements.title') }}
        </v-toolbar-title>
        <template #append>
          <v-btn
            icon="mdi-close"
            variant="text"
            density="comfortable"
            :aria-label="t('announcements.close')"
            @click="drawerOpen = false"
          />
        </template>
      </v-toolbar>

      <v-sheet
        v-if="isAnnouncementsPending"
        class="d-flex justify-center pa-8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </v-sheet>

      <v-sheet
        v-else-if="announcements.length === 0"
        class="d-flex flex-column align-center pa-10 text-center"
      >
        <v-icon
          icon="mdi-bullhorn-outline"
          size="48"
          class="mb-3 text-medium-emphasis"
        />
        <span class="text-body-2 text-medium-emphasis">
          {{ t('announcements.empty') }}
        </span>
      </v-sheet>

      <v-list
        v-else
        class="py-0"
        bg-color="transparent"
      >
        <template
          v-for="(announcement, index) in announcements"
          :key="announcement.id"
        >
          <v-list-item class="py-4 px-6">
            <div class="d-flex align-center ga-2 mb-2">
              <v-chip
                :color="categoryColor[announcement.category]"
                size="x-small"
                variant="tonal"
                label
              >
                {{ announcement.humanCategory }}
              </v-chip>
              <span
                v-if="announcement.humanPublishedAt"
                class="text-caption text-medium-emphasis"
              >
                {{ announcement.humanPublishedAt }}
              </span>
            </div>

            <div class="text-subtitle-1 font-weight-bold mb-2">
              {{ announcement.title }}
            </div>

            <v-img
              v-if="announcement.imageUrl"
              :src="announcement.imageUrl"
              :aspect-ratio="16 / 9"
              cover
              rounded="lg"
              class="my-3"
            />

            <div class="text-body-2 text-medium-emphasis announcement-body">
              {{ announcement.body }}
            </div>
          </v-list-item>

          <v-divider v-if="index < announcements.length - 1" />
        </template>
      </v-list>
    </v-navigation-drawer>
  </Teleport>
</template>

<style scoped>
.announcement-body {
  white-space: pre-wrap;
}

.announcements-badge :deep(.v-badge__badge) {
  font-weight: 600;
  font-size: .65rem;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-shadow: 0 0 0 2px rgb(var(--v-theme-surface));
}
</style>

